import { useEffect, useRef, useState } from "react";
import { Check, Image as ImageIcon, Loader2, Upload, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminBanner, AdminButton } from "@/components/admin/AdminUi";

const ACCEPT = {
  images: "image/jpeg,image/png,image/webp,image/gif,image/svg+xml",
  videos: "video/mp4",
};

export function canonicalMediaPath(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/\/api\/media\/([^/?#]+)\/file/i);
  return match ? `/api/media/${match[1]}/file` : raw;
}

export function normalizeMediaRefs(value) {
  if (Array.isArray(value)) return value.map(normalizeMediaRefs);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => {
        if (
          typeof child === "string" &&
          (key === "media_url" || key === "image_url" || key === "hero_image_url" || key.endsWith("_image"))
        ) {
          return [key, canonicalMediaPath(child)];
        }
        return [key, normalizeMediaRefs(child)];
      })
    );
  }
  return value;
}

function selectedId(value = "") {
  const match = String(value || "").match(/\/api\/media\/([^/?#]+)\/file/i);
  return match?.[1] || "";
}

export default function MediaPicker({
  value = "",
  onChange,
  kind = "images",
  label,
  buttonLabel,
  testId = "cms-media-picker",
  autoOpen = false,
  onAutoOpenHandled,
}) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const fileRef = useRef(null);

  const isVideo = kind === "videos";
  const normalized = canonicalMediaPath(value);
  const activeId = selectedId(normalized);
  const friendlyLabel = label || (isVideo ? "Video" : "Picture");

  const load = () => {
    setItems(null);
    api
      .get("/admin/media", { params: { kind } })
      .then((r) => setItems(r.data || []))
      .catch((e) => {
        setItems([]);
        setBanner({ status: "error", message: apiError(e) });
      });
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);

  useEffect(() => {
    if (!autoOpen) return;
    setOpen(true);
    onAutoOpenHandled?.();
  }, [autoOpen, onAutoOpenHandled]);

  const choose = (item) => {
    onChange?.(`/api/media/${item.id}/file`);
    setOpen(false);
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setBanner({ status: "idle", message: "" });

    const body = new FormData();
    body.append("file", file);

    try {
      const { data } = await api.post("/admin/media", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.id) throw new Error("Upload completed but no media record was returned.");
      onChange?.(`/api/media/${data.id}/file`);
      setOpen(false);
    } catch (e) {
      setBanner({ status: "error", message: apiError(e, e?.message || "Upload failed.") });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      {label && <p className="mb-2 text-sm font-bold text-bka-navy">{label}</p>}

      {normalized ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex min-h-40 items-center justify-center bg-slate-100">
            {isVideo ? (
              <video src={mediaUrl(normalized)} className="max-h-56 w-full object-cover" muted controls />
            ) : (
              <img src={mediaUrl(normalized)} alt="Selected" className="h-48 w-full object-cover" />
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 p-3">
            <div>
              <p className="text-sm font-bold text-bka-navy">{friendlyLabel} attached</p>
              <p className="text-xs text-slate-500">No link or URL is required.</p>
            </div>
            <div className="flex gap-2">
              <AdminButton type="button" variant="ghost" onClick={() => setOpen(true)} className="px-3 py-1.5 text-xs">
                Change {isVideo ? "Video" : "Picture"}
              </AdminButton>
              <button
                type="button"
                onClick={() => onChange?.("")}
                className="rounded-lg px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-36 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-5 py-7 text-center transition hover:border-bka-emerald hover:bg-emerald-50/30"
        >
          <ImageIcon className="mb-2 h-8 w-8 text-bka-emerald" />
          <span className="text-base font-bold text-bka-navy">
            {buttonLabel || `Choose or Upload ${isVideo ? "Video" : "Picture"}`}
          </span>
          <span className="mt-1 text-sm text-slate-500">
            Click here — no copying or pasting.
          </span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-bka-emerald">Step 1 of 1</p>
                <h2 className="mt-1 font-display text-2xl font-bold text-bka-navy">
                  Choose a {isVideo ? "Video" : "Picture"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Upload a new file or click one that is already here. It will be attached automatically.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500"
                aria-label="Close media picker"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="border-b border-slate-100 bg-slate-50 px-6 py-5">
              <AdminBanner
                status={banner.status}
                message={banner.message}
                onDismiss={() => setBanner({ status: "idle", message: "" })}
              />
              {can("media.write") && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPT[kind]}
                    onChange={upload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="flex min-h-20 w-full items-center justify-center gap-3 rounded-2xl bg-bka-emerald px-5 py-4 text-base font-extrabold text-white shadow-sm transition hover:brightness-95 disabled:opacity-60"
                  >
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                    {uploading
                      ? "Uploading…"
                      : `Upload a New ${isVideo ? "Video" : "Picture"} From This Computer`}
                  </button>
                </>
              )}
            </div>

            <div className="max-h-[58vh] overflow-y-auto p-6">
              <h3 className="mb-4 text-base font-extrabold text-bka-navy">
                Or choose one already uploaded
              </h3>

              {items === null ? (
                <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                  <p className="font-bold text-bka-navy">No files have been uploaded yet.</p>
                  <p className="mt-2 text-sm text-slate-500">Use the large upload button above.</p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {items.map((item) => {
                    const path = `/api/media/${item.id}/file`;
                    const active = activeId === item.id;
                    return (
                      <button
                        type="button"
                        key={item.id}
                        onClick={() => choose(item)}
                        className={`group overflow-hidden rounded-2xl border bg-white text-left transition hover:-translate-y-0.5 hover:shadow-md ${
                          active ? "border-bka-emerald ring-2 ring-bka-emerald/20" : "border-slate-200"
                        }`}
                      >
                        <div className="relative h-36 bg-slate-100">
                          {isVideo ? (
                            <video src={mediaUrl(path)} className="h-full w-full object-cover" muted preload="metadata" />
                          ) : (
                            <img
                              src={mediaUrl(path)}
                              alt={item.alt_text || item.original_filename || "Picture"}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          )}
                          {active && (
                            <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-bka-emerald text-white shadow">
                              <Check className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="truncate text-sm font-bold text-bka-navy">
                            {item.original_filename || (isVideo ? "Video" : "Picture")}
                          </p>
                          <p className="mt-1 text-xs font-semibold text-bka-emerald">
                            Click to use this {isVideo ? "video" : "picture"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
